import type {
  Fare,
  FareSliceDeparture,
  FareSliceOutbound,
  Flights,
  MulticityFlights,
} from "@b2bportal/air-shopping-api";
import type { IFlightListData } from "@b2bportal/core-types";
import dayjs from "dayjs";

// Sort by Stops first and then sort by departure time within stops grouping
export const orderByStopsThenDeparture = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  filteredFlights: IFlightListData<T>[],
  flights: Flights | MulticityFlights
) => {
  return filteredFlights.sort((a, b) => {
    if (flights.slices[a.sliceId].stops === flights.slices[b.sliceId].stops) {
      return dayjs(flights.slices[a.sliceId].departure).diff(
        dayjs(flights.slices[b.sliceId].departure)
      );
    } else {
      return flights.slices[a.sliceId].stops - flights.slices[b.sliceId].stops;
    }
  });
};

export const orderByRecommendedV2 = (
  filteredFlights: IFlightListData<
    Fare | FareSliceOutbound | FareSliceDeparture
  >[],
  flights: Flights
) => {
  return filteredFlights.sort(
    (a, b) =>
      flights.slices[b.sliceId].highestScore.value -
      flights.slices[a.sliceId].highestScore.value
  );
};

export const orderByRecommendedV3 = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  filteredFlights: IFlightListData<T>[],
  flights: Flights | MulticityFlights,
  isReturn: boolean
) => {
  const fareSlicesWithTags = Object.keys({ ...flights?.fareSlices })
    .filter(
      (key) =>
        flights.fareSlices[key].tags?.isBest ||
        flights.fareSlices[key].tags?.isBestQuality ||
        flights.fareSlices[key].tags?.isCheapest ||
        flights.fareSlices[key].tags?.isFastest
    )
    .reduce((res, fare) => {
      const slice = flights.fareSlices[fare].slice;
      res[slice] = flights.fareSlices[fare];
      return res;
    }, {});

  return filteredFlights.sort((a, b) => {
    const sliceA = flights.slices[a.sliceId];
    const sliceB = flights.slices[b.sliceId];
    const idA = sliceA.id;
    const idB = sliceB.id;

    const lowestPriceA = "lowestPrice" in sliceA ? sliceA.lowestPrice : 0;
    const lowestPriceB = "lowestPrice" in sliceB ? sliceB.lowestPrice : 0;
    const highestScoreA =
      "highestScore" in sliceA ? sliceA.highestScore.value : 0;
    const highestScoreB =
      "highestScore" in sliceB ? sliceB.highestScore.value : 0;

    const aFlightHasTags = idA in fareSlicesWithTags;
    const bFlightHasTags = idB in fareSlicesWithTags;

    if (aFlightHasTags && Boolean(fareSlicesWithTags[idA].tags?.isBest)) {
      return -1;
    } else if (
      bFlightHasTags &&
      Boolean(fareSlicesWithTags[idB].tags?.isBest)
    ) {
      return 1;
    }

    if (aFlightHasTags && bFlightHasTags) {
      return (
        (lowestPriceA ?? Number.MAX_SAFE_INTEGER) -
        (lowestPriceB ?? Number.MAX_SAFE_INTEGER)
      );
    } else if (aFlightHasTags) {
      return -1;
    } else if (bFlightHasTags) {
      return 1;
    } else {
      return highestScoreB - highestScoreA;
    }
  });
};

export const orderByPriceV2 = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  filteredFlights: IFlightListData<T>[]
) => {
  return filteredFlights.sort(
    (a, b) =>
      Math.min(
        ...a.fares.map(
          (f) =>
            f.amount?.fiat.value ??
            f.priceBreakdown?.taxesAndFees.fiat.value ??
            0
        )
      ) -
      Math.min(
        ...b.fares.map(
          (f) =>
            f.amount?.fiat.value ??
            ("priceBreakdown" in f
              ? (f.priceBreakdown?.taxesAndFees.fiat.value ?? 0)
              : 0)
        )
      )
  );
};

export const orderByDepartureTimeV2 = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  filteredFlights: IFlightListData<T>[],
  flights: Flights | MulticityFlights
) => {
  return filteredFlights.sort((a, b) =>
    dayjs(flights.slices[a.sliceId].departure).diff(
      dayjs(flights.slices[b.sliceId].departure)
    )
  );
};

export const orderByArrivalTimeV2 = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  filteredFlights: IFlightListData<T>[],
  flights: Flights | MulticityFlights
) => {
  return filteredFlights.sort((a, b) =>
    dayjs(flights.slices[a.sliceId].arrival).diff(
      dayjs(flights.slices[b.sliceId].arrival)
    )
  );
};

export const orderByStopsV2 = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  filteredFlights: IFlightListData<T>[],
  flights: Flights | MulticityFlights
) => {
  return filteredFlights.sort(
    (a, b) => flights.slices[a.sliceId].stops - flights.slices[b.sliceId].stops
  );
};

export const orderByDurationV2 = <
  T extends Fare | FareSliceOutbound | FareSliceDeparture,
>(
  filteredFlights: IFlightListData<T>[],
  flights: Flights | MulticityFlights
) => {
  return filteredFlights.sort((a, b) => {
    const aMin = dayjs(flights.slices[a.sliceId].arrival).diff(
      dayjs(flights.slices[a.sliceId].departure),
      "minute",
      true
    );
    const bMin = dayjs(flights.slices[b.sliceId].arrival).diff(
      dayjs(flights.slices[b.sliceId].departure),
      "minute",
      true
    );

    return aMin - bMin;
  });
};

// Flight Shop V3
